import React, {useCallback, useState} from 'react';

import {useSubmitPdfConsentMutation} from '~/store/rtkQuery';
import {AuditLogButton} from '~/features/AuditLog/AuditLogButton';
import {FoundParentSummary} from './FoundParentSummary';
import {useParentLookup} from './useParentLookup';

const SUBMIT_ERROR_MESSAGES: Record<string, string> = {
  PARENT_NAME_MISMATCH:
    "The parent's name on the form doesn't match the name on their account. Double-check spelling on both sides before retrying, or reach out to the parent to check.",
  INVALID_TEEN:
    'This teen could not be found on the parent’s account. Try searching again.',
};

const getSubmitErrorMessage = (message?: string) => {
  if (!message) {
    return undefined;
  }
  return SUBMIT_ERROR_MESSAGES[message] ?? message;
};

export const PdfConsentSubmission = () => {
  const {parentLookup, search, reset: resetParentLookup} = useParentLookup();
  const [submitPdfConsent, {error: submitError, isLoading: isSubmitting}] =
    useSubmitPdfConsentMutation();

  const [email, setEmail] = useState('');

  const [teenId, setTeenId] = useState('');
  const [signedDate, setSignedDate] = useState('');
  const [parentNameOnForm, setParentNameOnForm] = useState('');
  const [teenNameOnForm, setTeenNameOnForm] = useState('');
  const [consentFormVersion, setConsentFormVersion] = useState('');
  const [signatureConfirmed, setSignatureConfirmed] = useState(false);
  const [sourceReference, setSourceReference] = useState('');

  const [inviteCode, setInviteCode] = useState('');

  const onReset = useCallback(() => {
    setEmail('');
    resetParentLookup();
    setTeenId('');
    setSignedDate('');
    setParentNameOnForm('');
    setTeenNameOnForm('');
    setConsentFormVersion('');
    setSignatureConfirmed(false);
    setSourceReference('');
    setInviteCode('');
  }, [resetParentLookup]);

  const onSearch = useCallback(() => {
    setTeenId('');
    search(email);
  }, [email, search]);

  const isSearching = parentLookup.status === 'searching';
  const teens =
    parentLookup.status === 'found' ? parentLookup.parentInfo.teens ?? [] : [];

  const canSubmit =
    !!teenId &&
    !!signedDate &&
    !!parentNameOnForm &&
    !!teenNameOnForm &&
    !!consentFormVersion &&
    !!sourceReference &&
    signatureConfirmed;

  const onSubmit = useCallback(async () => {
    if (parentLookup.status !== 'found') {
      return;
    }

    const response = await submitPdfConsent({
      parentUserId: parentLookup.parentUserId,
      teenId,
      pdfConsentDetails: {
        signedDate,
        parentNameOnForm,
        teenNameOnForm,
        consentFormVersion,
        signatureConfirmed,
        sourceReference,
      },
    });

    if (!('error' in response)) {
      setInviteCode(response.data.inviteCode);
    }
  }, [
    submitPdfConsent,
    parentLookup,
    teenId,
    signedDate,
    parentNameOnForm,
    teenNameOnForm,
    consentFormVersion,
    signatureConfirmed,
    sourceReference,
  ]);

  if (inviteCode) {
    return (
      <div className="p-5">
        <h1 className="text-2xl font-semibold">Record PDF Consent</h1>
        <div role="alert" className="alert alert-success mt-4">
          <span>Consent recorded. Invite code: {inviteCode}</span>
        </div>
        <button className="btn btn-primary mt-4" onClick={onReset}>
          Submit another
        </button>

        <AuditLogButton
          endpoints={['POST /parentConfig/minorInviteConsent']}
        />
      </div>
    );
  }

  return (
    <div className="p-5">
      <h1 className="text-2xl font-semibold">Record PDF Consent</h1>
      <p className="mt-2 text-sm text-gray-600">
        For consent captured on a signed PDF form instead of the app's magic
        link invite. Fill in the fields from the PDF form below to record
        consent and generate an invite code.
      </p>

      <div className="mt-4 form-control max-w-md">
        <label className="label" htmlFor="parent-email">
          <span className="label-text font-medium">Parent's email</span>
        </label>
        <input
          id="parent-email"
          className="input input-bordered w-full"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <button
          className={`btn mt-2 ${
            email.trim() && !isSearching ? 'btn-primary' : 'btn-outline'
          }`}
          onClick={onSearch}
          disabled={!email.trim() || isSearching}>
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </div>

      {parentLookup.status === 'error' && (
        <div role="alert" className="alert alert-error mt-4 max-w-md">
          <span>{parentLookup.message}</span>
        </div>
      )}

      {parentLookup.status === 'found' && (
        <div className="mt-4 max-w-md rounded-lg bg-base-200 p-3">
          <FoundParentSummary parentLookup={parentLookup} />

          {teens.length === 0 ? (
            <p className="mt-2 text-red-600">
              This parent has no teens on their account.
            </p>
          ) : (
            <div className="form-control mt-2">
              <label className="label" htmlFor="teen-select">
                <span className="label-text">Teen</span>
              </label>
              <select
                id="teen-select"
                className="select select-bordered w-full"
                value={teenId}
                onChange={e => setTeenId(e.target.value)}>
                <option value="">--- select teen ---</option>
                {teens.map(teen => {
                  // Invited/claimed teens already have consent on record —
                  // selecting them here would just reissue their existing
                  // invite silently, without creating a new consent record.
                  const alreadyConsented =
                    teen.status === 'invited' || teen.status === 'claimed';
                  return (
                    <option
                      key={teen.id}
                      value={teen.id}
                      disabled={alreadyConsented}>
                      {teen.teenFirstName} {teen.teenLastName}
                      {teen.status ? ` (${teen.status})` : ''}
                      {alreadyConsented ? ' — consent already given' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      )}

      {!!teenId && (
        <div className="mt-6 max-w-md">
          <h2 className="text-lg font-semibold">Fields from the signed PDF</h2>

          <div className="form-control">
            <label className="label" htmlFor="signed-date">
              <span className="label-text">Signed date</span>
            </label>
            <input
              id="signed-date"
              className="input input-bordered w-full"
              type="date"
              value={signedDate}
              onChange={e => setSignedDate(e.target.value)}
            />
          </div>
          <div className="form-control mt-3">
            <label className="label" htmlFor="parent-name-on-form">
              <span className="label-text">Parent's name on form</span>
            </label>
            <input
              id="parent-name-on-form"
              className="input input-bordered w-full"
              type="text"
              value={parentNameOnForm}
              onChange={e => setParentNameOnForm(e.target.value)}
            />
          </div>
          <div className="form-control mt-3">
            <label className="label" htmlFor="teen-name-on-form">
              <span className="label-text">Teen's name on form</span>
            </label>
            <input
              id="teen-name-on-form"
              className="input input-bordered w-full"
              type="text"
              value={teenNameOnForm}
              onChange={e => setTeenNameOnForm(e.target.value)}
            />
          </div>
          <div className="form-control mt-3">
            <label className="label" htmlFor="consent-form-version">
              <span className="label-text">Consent form version</span>
            </label>
            <input
              id="consent-form-version"
              className="input input-bordered w-full"
              type="text"
              value={consentFormVersion}
              onChange={e => setConsentFormVersion(e.target.value)}
            />
          </div>
          <div className="form-control mt-3">
            <label className="label" htmlFor="source-reference">
              <span className="label-text">Source reference</span>
            </label>
            <input
              id="source-reference"
              className="input input-bordered w-full"
              type="text"
              placeholder="e.g. support ticket ID, Drive link"
              value={sourceReference}
              onChange={e => setSourceReference(e.target.value)}
            />
          </div>
          <div className="form-control py-4">
            <label className="label cursor-pointer justify-start gap-3">
              <input
                type="checkbox"
                checked={signatureConfirmed}
                className="checkbox checkbox-primary"
                onChange={e => setSignatureConfirmed(e.target.checked)}
              />
              <span className="label-text">
                I have visually confirmed the parent's signature on the form
              </span>
            </label>
          </div>

          {!!submitError && (
            <div role="alert" className="alert alert-error">
              <span>{getSubmitErrorMessage(submitError.message)}</span>
            </div>
          )}

          <button
            className={`btn ${
              canSubmit && !isSubmitting ? 'btn-primary' : 'btn-outline'
            }`}
            onClick={onSubmit}
            disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      )}

      <AuditLogButton endpoints={['POST /parentConfig/minorInviteConsent']} />
    </div>
  );
};
